<script setup>
/**
 * SzCrmPanel — embed do micro-app SolarZ CRM (/sz-crm/) na tab CRM do
 * menu de contato. O iframe se auto-gerencia (setup/config/ficha/list/
 * search) e comunica com o parent via postMessage:
 *   - 'sz-crm:resize'        { height }  → ajusta altura do iframe
 *   - 'sz-crm:gear-visibility' { visible } (ignorado no layout de tab)
 *   - 'sz-crm:title-suffix'  { name }    (ignorado)
 *   - 'sz-crm:view-changed'  { view }    (ignorado)
 *
 * URL (igual ao embed legado, conversation_id = display_id):
 *   /sz-crm/?account_id&conversation_id&theme&v&agent_id&agent_name
 *
 * Marker: SZ_CRM_PANEL_20260528
 */
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';

const CRM_VERSION = '20260528a';

const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const accountId = computed(() => store.getters.getCurrentAccountId);
const currentUser = computed(() => store.getters.getCurrentUser);

const iframeHeight = ref(220);

const isDark = ref(
  document.documentElement.classList.contains('dark') ||
  document.body.classList.contains('dark')
);
const theme = computed(() => (isDark.value ? 'dark' : 'light'));

// conversation_id = display_id (numeração por conta), igual ao embed legado.
const convDisplayId = computed(
  () => currentChat.value?.display_id || currentChat.value?.id || ''
);
const agentId = computed(() => currentUser.value?.id || '');
const agentName = computed(
  () => currentUser.value?.name || currentUser.value?.available_name || ''
);

const iframeSrc = computed(() => {
  const p = new URLSearchParams({
    account_id: String(accountId.value || '0'),
    conversation_id: String(convDisplayId.value || ''),
    theme: theme.value,
    v: CRM_VERSION,
    agent_id: String(agentId.value || '0'),
    agent_name: agentName.value || '',
  });
  return `/sz-crm/?${p.toString()}`;
});

// Força recriação do iframe ao trocar de conversa/agente.
const convKey = computed(
  () => `${accountId.value}/${convDisplayId.value}|a${agentId.value}`
);

function onMessage(e) {
  const d = e && e.data;
  if (!d || typeof d !== 'object') return;
  if (d.type === 'sz-crm:resize' && typeof d.height === 'number') {
    iframeHeight.value = Math.max(120, Math.round(d.height));
  }
}

let darkObs = null;
onMounted(() => {
  window.addEventListener('message', onMessage);
  darkObs = new MutationObserver(() => {
    const d =
      document.documentElement.classList.contains('dark') ||
      document.body.classList.contains('dark');
    if (d !== isDark.value) isDark.value = d;
  });
  darkObs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  darkObs.observe(document.body, { attributes: true, attributeFilter: ['class'] });
});
onBeforeUnmount(() => {
  window.removeEventListener('message', onMessage);
  if (darkObs) darkObs.disconnect();
});
</script>

<template>
  <div class="sz-crm-panel">
    <iframe
      :key="convKey"
      class="sz-crm-iframe"
      :src="iframeSrc"
      :data-conv-key="convKey"
      allow="clipboard-write"
      :style="{ height: iframeHeight + 'px' }"
    />
  </div>
</template>

<style scoped>
.sz-crm-panel { width: 100%; }
.sz-crm-iframe {
  width: 100%;
  border: 0;
  display: block;
  border-radius: 0;
  background: transparent;
  margin: 0;
  transition: height 0.18s ease;
}
</style>
